import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { api } from "../api/client.ts"
import { useApiQuery } from "../hooks/index.ts"
import { useI18n } from "../i18n/index.tsx"
import { Badge, Icon, Menu, type BadgeTone } from "../ui/index.ts"

export type Notification = {
  id: number
  kind: string
  severity: "info" | "warn" | "danger"
  payload: { count?: number; days?: number } | null
  link: string | null
  createdAt: string
  readAt: string | null
}

/** 급한 정도 → 색. 배지와 같은 이름을 쓴다. */
const TONE: Record<Notification["severity"], BadgeTone> = {
  info: "info",
  warn: "warn",
  danger: "danger",
}

const SEVERITY_ICON: Record<Notification["severity"], "info" | "clock" | "alert"> = {
  info: "info",
  warn: "clock",
  danger: "alert",
}

/** 2분마다 한 번. 이보다 자주 물으면 서버만 두드리고, 더 뜸하면 벨이 낡는다. */
const POLL_MS = 120_000

/** 99 를 넘으면 숫자보다 "많다"가 정보다. */
function badgeText(unread: number): string {
  return unread > 99 ? "99+" : String(unread)
}

/**
 * 머리에 달린 알림 벨.
 *
 * 무엇을 알릴지는 서버(`server/src/domain/notification-rules.ts`)가 정하고, 여기서는
 * 종류(`kind`)와 숫자로 문구를 찾아 그린다 — 서버는 번역된 문장을 보내지 않는다.
 */
export function NotificationBell() {
  const { t } = useI18n()
  const navigate = useNavigate()
  const query = useApiQuery<{ items: Notification[]; unread: number }>("/api/notifications")
  const [tick, setTick] = useState(0)

  useEffect(() => {
    const timer = setInterval(() => setTick((value) => value + 1), POLL_MS)
    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    if (tick > 0) void query.reload()
    // 타이머가 돌 때만 다시 읽는다(query 를 의존성에 넣으면 매 렌더마다 다시 읽는다).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick])

  const items = query.data?.items ?? []
  const unread = query.data?.unread ?? 0

  async function open(item: Notification) {
    if (!item.readAt) await api.post(`/api/notifications/${item.id}/read`).catch(() => {})
    await query.reload()
    if (item.link) void navigate(item.link)
  }

  return (
    <div className="bell">
      <Menu label={t("notify.title")} icon="bell" iconOnly>
        <div className="bell__head">
          <strong>{t("notify.title")}</strong>
          {unread > 0 ? (
            <button
              type="button"
              className="bell__all"
              onClick={() => {
                void api.post("/api/notifications/read-all").then(() => query.reload())
              }}
            >
              {t("notify.readAll")}
            </button>
          ) : null}
        </div>

        {items.length === 0 ? (
          <p className="bell__empty">{t("notify.empty")}</p>
        ) : (
          <ul className="bell__list">
            {items.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  className={item.readAt ? "bell__item is-read" : "bell__item"}
                  onClick={() => void open(item)}
                >
                  <Icon name={SEVERITY_ICON[item.severity]} size={15} className="bell__icon" />
                  <span className="bell__text">
                    <span className="bell__title">
                      {t(`notify.kind.${item.kind}`, {
                        count: item.payload?.count ?? 0,
                        days: item.payload?.days ?? 0,
                      })}
                    </span>
                    <span className="bell__when">{item.createdAt.slice(0, 10)}</span>
                  </span>
                  <Badge tone={TONE[item.severity]} size="sm">
                    {item.payload?.count ?? 0}
                  </Badge>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Menu>

      {unread > 0 ? (
        <span className="bell__count" aria-label={t("notify.unread", { count: unread })}>
          {badgeText(unread)}
        </span>
      ) : null}
    </div>
  )
}
